import React from 'react';
import { Wrench, Shield, Package, CheckCircle, ArrowRight, Phone, FileText, Clock, TrendingDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const MaintenanceQuai = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <SEO 
        title="Maintenance des Équipements de Quai | Niveleurs & Portes Industrielles | ORION INDUSTRIE"
        description="Entretien, révision et réparation de niveleurs de quai, portes sectionnelles et rideaux métalliques. Packs maintenance, contrats d'entretien et dépannage. Intervention nationale."
        keywords="maintenance quai chargement, entretien niveleur quai, révision niveleur hydraulique, maintenance porte sectionnelle industrielle, contrat entretien quai, pack maintenance quai, réparation niveleur, dépannage quai"
      />
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-r from-[#020d2a] to-slate-800 text-white py-20 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <Wrench className="w-12 h-12 text-[#FF6600]" />
            <h1 className="text-4xl sm:text-5xl font-bold">
              Maintenance des Équipements de Quai
            </h1>
          </div>
          <p className="text-2xl text-orange-400 font-semibold mb-4">
            Entretenez vos quais, préservez votre activité
          </p>
          <p className="text-xl text-slate-300 max-w-4xl mb-8">
            Niveleurs de quai, portes sectionnelles, rideaux métalliques et équipements périphériques : 
            ORION INDUSTRIE vous accompagne dans l'entretien, la réparation et la remise en état de vos installations logistiques.
          </p>
          <div className="flex flex-wrap gap-4">
            <Button 
              onClick={() => scrollToSection('pack-maintenance')}
              size="lg"
              className="bg-[#FF6600] hover:bg-[#E65A00] text-white group"
            >
              Découvrir nos packs maintenance
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <a href="tel:+33659452562">
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-gray-300 text-white hover:bg-white/10"
              >
                <Phone className="mr-2 w-5 h-5" />
                Dépannage : 06 59 45 25 62
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Offre 1 - Révision Niveleur */}
      <section id="revision-niveleur" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            
            <h2 className="text-4xl font-bold text-slate-900 mt-3 mb-4">
              Forfait Révision Niveleur de Quai
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Entretien préventif des niveleurs de quai hydrauliques et mécaniques pour prolonger leur durée de vie
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Prestations selon configuration</h3>
              <div className="space-y-3">
                {[
                  'Inspection mécanique et visuelle complète',
                  'Vérification des vérins hydrauliques',
                  'Vérification des flexibles, joints et raccords',
                  'Recherche de fuites et points de fragilité',
                  'Contrôle du niveau et qualité de l\'huile hydraulique',
                  'Graissage et lubrification des points d\'articulation',
                  'Contrôle des axes, charnières et articulations',
                  'Vérification des fixations et ancrages',
                  'Contrôle des dispositifs de sécurité concernés',
                  'Essais fonctionnels dans des conditions sûres',
                  'Rapport d\'intervention détaillé avec réserves éventuelles'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="bg-orange-50 border-l-4 border-[#FF6600] p-6 rounded-lg mb-6">
                <h4 className="font-bold text-slate-900 mb-2">Non inclus au forfait</h4>
                <p className="text-slate-600 text-sm">
                  Les appoints d'huile, remplacements de pièces usées et réparations nécessaires 
                  font l'objet d'un chiffrage séparé après diagnostic.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
                <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#FF6600]" />
                  Formules disponibles
                </h4>
                <ul className="space-y-3 text-slate-700">
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-[#FF6600]">•</span>
                    <span><strong>Révision ponctuelle</strong> : intervention à la demande</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-[#FF6600]">•</span>
                    <span><strong>Entretien programmé</strong> : visites planifiées selon votre activité</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-[#FF6600]">•</span>
                    <span><strong>Contrat annuel</strong> : maintenance régulière avec tarif préférentiel</span>
                  </li>
                </ul>
              </div>

              <Button 
                onClick={() => scrollToSection('contact')}
                className="w-full bg-[#FF6600] hover:bg-[#E65A00] text-white"
                size="lg"
              >
                Demander un devis de révision
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Offre 2 - Maintenance Portes */}
      <section id="maintenance-portes" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            
            <h2 className="text-4xl font-bold text-slate-900 mt-3 mb-4">
              Maintenance Portes Industrielles
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Entretien des portes sectionnelles, rideaux métalliques et fermetures industrielles
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Portes Sectionnelles</h3>
              <p className="text-slate-600 mb-4">
                Entretien des mécanismes, guidages, articulations, ressorts et dispositifs de sécurité.
              </p>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Contrôle des panneaux et jonctions</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Vérification des ressorts et contrepoids</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Lubrification des rails et charnières</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Test des dispositifs de sécurité</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <Wrench className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Rideaux Métalliques</h3>
              <p className="text-slate-600 mb-4">
                Maintenance des lames, coulisses, motorisations et organes de commande.
              </p>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Contrôle des lames et agrafes</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Vérification des coulisses</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Graissage et lubrification</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Test moteur et automatismes</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-slate-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Contrats d'Entretien</h3>
              <p className="text-slate-600 mb-4">
                Périodicité conforme aux exigences réglementaires selon le type d'équipement.
              </p>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Visites programmées</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Traçabilité des interventions</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Tarif annuel maîtrisé</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Possibilité de mensualisation</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-lg max-w-4xl mx-auto">
            <p className="text-slate-700">
              <strong className="text-slate-900">Important :</strong> Les interventions sur organes sous tension, 
              ressorts, motorisations ou dispositifs de sécurité sont réalisées par des personnes compétentes 
              selon les normes en vigueur.
            </p>
          </div>
        </div>
      </section>

      {/* Offre 3 - Pack Maintenance Quai ⭐ */}
      <section id="pack-maintenance" className="py-20 bg-gradient-to-br from-[#FF6600] to-[#E65A00] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block bg-white/20 px-4 py-2 rounded-full mb-4">
              <span className="font-semibold text-sm uppercase tracking-wider">⭐ Offre Prioritaire</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              Pack Maintenance Quai
            </h2>
            <p className="text-2xl text-orange-100 max-w-3xl mx-auto">
              Un seul interlocuteur pour l'entretien de votre porte industrielle et de votre niveleur de quai
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <h3 className="text-3xl font-bold mb-6">Simplifiez votre maintenance</h3>
              <p className="text-xl text-orange-100 mb-8">
                Une solution complète qui regroupe l'entretien du niveleur et de la porte sectionnelle ou du rideau métallique associé.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4 bg-white/10 rounded-lg p-4">
                  <Package className="w-8 h-8 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-lg mb-1">Révision du niveleur</h4>
                    <p className="text-orange-100 text-sm">Inspection complète, graissage, contrôles hydrauliques et fonctionnels</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-white/10 rounded-lg p-4">
                  <Package className="w-8 h-8 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-lg mb-1">Entretien de la porte</h4>
                    <p className="text-orange-100 text-sm">Maintenance de la porte sectionnelle ou du rideau métallique</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-white/10 rounded-lg p-4">
                  <FileText className="w-8 h-8 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-lg mb-1">Rapport d'intervention</h4>
                    <p className="text-orange-100 text-sm">Document détaillé avec observations et recommandations</p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={() => scrollToSection('contact')}
                size="lg"
                className="bg-white text-[#FF6600] hover:bg-gray-100 w-full sm:w-auto"
              >
                Demander une offre Pack Maintenance
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>

            <div className="bg-white rounded-2xl p-8 text-slate-900 shadow-2xl">
              <h3 className="text-2xl font-bold mb-6 text-center">Avantages du Pack</h3>
              <div className="space-y-4">
                {[
                  { icon: <Clock className="w-6 h-6" />, title: 'Gain de temps', desc: 'Une seule intervention pour deux équipements' },
                  { icon: <TrendingDown className="w-6 h-6" />, title: 'Optimisation des coûts', desc: 'Déplacements mutualisés sur le pack' },
                  { icon: <Shield className="w-6 h-6" />, title: 'Coordination assurée', desc: 'Maintenance synchronisée de vos équipements' },
                  { icon: <FileText className="w-6 h-6" />, title: 'Suivi centralisé', desc: 'Un seul rapport pour l\'ensemble du poste de chargement' }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className="text-[#FF6600]">{item.icon}</div>
                    <div>
                      <h4 className="font-bold mb-1">{item.title}</h4>
                      <p className="text-sm text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Offre 4 - Pack Complet */}
      <section id="pack-complet" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            
            <h2 className="text-4xl font-bold text-slate-900 mt-3 mb-4">
              Pack Maintenance Quai Complet
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Maintenance coordonnée de l'ensemble de votre installation logistique
            </p>
          </div>

          <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 lg:p-12 shadow-xl max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Pack Maintenance Quai +</h3>
                <p className="text-slate-600 mb-6">
                  Reprend toutes les prestations du Pack Maintenance Quai et ajoute, selon votre installation :
                </p>
                <div className="space-y-3">
                  {[
                    'Butoirs de quai',
                    'Abris et sas d\'étanchéité',
                    'Protections métalliques de quai',
                    'Guides-roues et bordures',
                    'Barrières et dispositifs de protection',
                    'Fixations et éléments périphériques'
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#FF6600] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Pour qui ?</h3>
                <p className="text-slate-600 mb-6">
                  Cette formule s'adresse aux installations logistiques complètes nécessitant une maintenance 
                  coordonnée de tous les équipements de quai.
                </p>

                <div className="bg-orange-50 border-l-4 border-[#FF6600] p-6 rounded-lg mb-6">
                  <h4 className="font-bold text-slate-900 mb-2">Définition sur mesure</h4>
                  <p className="text-slate-600 text-sm">
                    Les opérations réalisées, la périodicité et les exclusions sont définies précisément 
                    dans le devis ou le contrat, en fonction de vos équipements.
                  </p>
                </div>

                <Button 
                  onClick={() => scrollToSection('contact')}
                  className="w-full bg-[#FF6600] hover:bg-[#E65A00] text-white"
                  size="lg"
                >
                  Obtenir un devis personnalisé
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Offre 5 - Dépannage */}
      <section id="depannage" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            
            <h2 className="text-4xl font-bold text-slate-900 mt-3 mb-4">
              Dépannage & Réparation
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Interventions correctives pour remettre en service vos équipements de quai
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              { title: 'Réparation de niveleur', items: ['Fuite hydraulique', 'Vérin défectueux', 'Centrale hydraulique', 'Problème électrique'] },
              { title: 'Remplacement de pièces', items: ['Vérins hydrauliques', 'Flexibles et raccords', 'Lèvres de niveleur', 'Joints et garnitures'] },
              { title: 'Réparation de porte', items: ['Ressorts cassés', 'Panneaux endommagés', 'Rails et guidages', 'Motorisation défaillante'] },
              { title: 'Rideau métallique', items: ['Lames abîmées', 'Blocage du rideau', 'Moteur en panne', 'Manivelle cassée'] },
              { title: 'Réparations mécaniques', items: ['Axes et articulations', 'Fixations et ancrages', 'Structures métalliques', 'Soudage sur site'] },
              { title: 'Mise en sécurité', items: ['Dispositifs de protection', 'Éléments de sécurité', 'Signalisation', 'Condamnation temporaire'] }
            ].map((category, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-[#FF6600]" />
                  {category.title}
                </h3>
                <ul className="space-y-2">
                  {category.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="text-[#FF6600]">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-[#020d2a] to-slate-800 rounded-2xl p-8 lg:p-12 text-white max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold mb-4">Besoin d'un dépannage ?</h3>
              <p className="text-xl text-slate-300">
                Diagnostic, recherche de solutions et remise en service de vos équipements
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white/10 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Phone className="w-6 h-6 text-[#FF6600]" />
                  <h4 className="font-bold text-lg">Dépannage urgent</h4>
                </div>
                <p className="text-slate-300 text-sm mb-3">
                  Intervention selon localisation pour remettre en service vos équipements critiques
                </p>
                <p className="text-orange-400 font-semibold">Dépannage en 24-48h selon localisation</p>
              </div>

              <div className="bg-white/10 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle className="w-6 h-6 text-[#FF6600]" />
                  <h4 className="font-bold text-lg">Notre approche</h4>
                </div>
                <p className="text-slate-300 text-sm">
                  Diagnostic précis, solutions adaptées et prolongation de la durée de vie 
                  lorsque la réparation est techniquement et économiquement pertinente
                </p>
              </div>
            </div>

            <div className="text-center">
              <a href="tel:+33659452562">
                <Button 
                  size="lg"
                  className="bg-[#FF6600] hover:bg-[#E65A00] text-white"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  Dépannage Urgent : 06 59 45 25 62
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Exemples de budgets */}
      <section id="exemples-budgets" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              À chaque installation, son budget de maintenance
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Nos prestations sont adaptées à vos équipements, à votre localisation et à la fréquence d'entretien nécessaire
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            {/* Config 1 */}
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 shadow-lg border-2 border-slate-200 hover:border-[#FF6600] transition-colors">
              <div className="text-center mb-6">
                <div className="inline-block bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  Configuration A
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  Révision ponctuelle d'un niveleur
                </h3>
                <p className="text-slate-600">Site proche de Nevers</p>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-[#FF6600]">•</span>
                  <span className="text-sm text-slate-700"><strong>Périmètre :</strong> 1 niveleur de quai</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-[#FF6600]">•</span>
                  <span className="text-sm text-slate-700"><strong>Prestations :</strong> Révision complète selon configuration</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-[#FF6600]">•</span>
                  <span className="text-sm text-slate-700"><strong>Fréquence :</strong> Intervention ponctuelle</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-[#FF6600]">•</span>
                  <span className="text-sm text-slate-700"><strong>Déplacement :</strong> Proximité Nevers (Nièvre)</span>
                </div>
              </div>

              <div className="bg-orange-50 border-l-4 border-[#FF6600] p-4 rounded mb-6">
                <h4 className="font-bold text-slate-900 text-sm mb-2">Facteurs d'ajustement</h4>
                <p className="text-xs text-slate-600">
                  Le tarif final intègre l'état de l'équipement, les prestations exactes selon configuration 
                  et les frais de déplacement inclus.
                </p>
              </div>

              <div className="bg-slate-100 rounded-lg p-4 mb-6 text-center">
                <p className="text-sm text-slate-600 mb-1">Budget</p>
                <p className="text-2xl font-bold text-[#FF6600]">Devis sur mesure</p>
                <p className="text-xs text-slate-500 mt-1">Appoints et pièces non inclus</p>
              </div>

              <p className="text-xs text-slate-600 mb-4">
                <strong>Avantages :</strong> Intervention rapide, rapport détaillé, prolongation de la durée de vie
              </p>
            </div>

            {/* Config 2 */}
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 shadow-lg border-2 border-slate-200 hover:border-[#FF6600] transition-colors">
              <div className="text-center mb-6">
                <div className="inline-block bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  Configuration B
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  Maintenance groupée de 5 niveleurs
                </h3>
                <p className="text-slate-600">Intervention régionale</p>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-[#FF6600]">•</span>
                  <span className="text-sm text-slate-700"><strong>Périmètre :</strong> 5 niveleurs sur un même site</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-[#FF6600]">•</span>
                  <span className="text-sm text-slate-700"><strong>Prestations :</strong> Révision complète par équipement</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-[#FF6600]">•</span>
                  <span className="text-sm text-slate-700"><strong>Fréquence :</strong> Visite annuelle programmée</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-[#FF6600]">•</span>
                  <span className="text-sm text-slate-700"><strong>Déplacement :</strong> Mutualisation sur 5 équipements</span>
                </div>
              </div>

              <div className="bg-orange-50 border-l-4 border-[#FF6600] p-4 rounded mb-6">
                <h4 className="font-bold text-slate-900 text-sm mb-2">Optimisation des coûts</h4>
                <p className="text-xs text-slate-600">
                  Interventions regroupées permettant une mutualisation des déplacements et un coût 
                  par équipement optimisé.
                </p>
              </div>

              <div className="bg-slate-100 rounded-lg p-4 mb-6 text-center">
                <p className="text-sm text-slate-600 mb-1">Budget annuel</p>
                <p className="text-2xl font-bold text-[#FF6600]">Devis sur mesure</p>
                <p className="text-xs text-slate-500 mt-1">Déplacements mutualisés</p>
              </div>

              <p className="text-xs text-slate-600 mb-4">
                <strong>Avantages :</strong> Maintenance coordonnée, traçabilité, coût par équipement réduit
              </p>
            </div>

            {/* Config 3 */}
            <div className="bg-gradient-to-br from-orange-50 to-white rounded-2xl p-8 shadow-xl border-2 border-[#FF6600]">
              <div className="text-center mb-6">
                <div className="inline-block bg-[#FF6600] text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  ⭐ Configuration C
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  Contrat d'entretien 20 quais complets
                </h3>
                <p className="text-slate-600">Plateforme logistique</p>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-[#FF6600]">•</span>
                  <span className="text-sm text-slate-700"><strong>Périmètre :</strong> 20 niveleurs + 20 portes sectionnelles</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-[#FF6600]">•</span>
                  <span className="text-sm text-slate-700"><strong>Prestations :</strong> Pack Maintenance Quai pour chaque quai de chargement</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-[#FF6600]">•</span>
                  <span className="text-sm text-slate-700"><strong>Fréquence :</strong> Visites programmées (2/an recommandé)</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-[#FF6600]">•</span>
                  <span className="text-sm text-slate-700"><strong>Déplacement :</strong> Inclus dans le contrat annuel</span>
                </div>
              </div>

              <div className="bg-white border-l-4 border-[#FF6600] p-4 rounded mb-6">
                <h4 className="font-bold text-slate-900 text-sm mb-2">Contrat avec mensualisation</h4>
                <p className="text-xs text-slate-600">
                  Budget annuel défini, interventions programmées et possibilité de règlement mensuel 
                  pour une meilleure gestion de trésorerie.
                </p>
              </div>

              <div className="bg-slate-100 rounded-lg p-4 mb-6 text-center">
                <p className="text-sm text-slate-600 mb-1">Budget annuel</p>
                <p className="text-2xl font-bold text-[#FF6600]">Devis sur mesure</p>
                <p className="text-xs text-slate-500 mt-1">Mensualisation possible</p>
              </div>

              <p className="text-xs text-slate-600 mb-4">
                <strong>Avantages :</strong> Budget maîtrisé, planification assurée, priorité dépannage, suivi centralisé
              </p>
            </div>
          </div>

          <div className="text-center">
            <p className="text-slate-600 mb-6">
              Chaque installation est unique. Le tarif définitif tient compte du nombre d'équipements, 
              de leur état, du lieu d'intervention, des prestations exactes et de la fréquence des visites.
            </p>
            <Button 
              onClick={() => scrollToSection('contact')}
              size="lg"
              className="bg-[#FF6600] hover:bg-[#E65A00] text-white"
            >
              Obtenir une estimation pour mon site
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section id="contact" className="py-20 bg-gradient-to-r from-[#020d2a] to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Prêt à mettre en place la maintenance de vos équipements de quai ?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Contactez-nous pour obtenir un devis adapté à votre installation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => window.location.href = '/#quote'}
              size="lg"
              className="bg-[#FF6600] hover:bg-[#E65A00] text-white"
            >
              Demander un devis
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a href="tel:+33659452562">
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10"
              >
                <Phone className="mr-2 w-5 h-5" />
                06 59 45 25 62
              </Button>
            </a>
          </div>
          <p className="text-sm text-slate-400 mt-6">
            Compatible toutes marques selon disponibilité des stocks fabricants
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MaintenanceQuai;
